import { useCallback, useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { fmtDate, type PlatformCall } from './common';

export interface PlatformNotification {
  id: number;
  kind: string;
  severity: 'info' | 'success' | 'warning' | 'error';
  title: string;
  body: string | null;
  tenant_key: string | null;
  ref_type: string | null;
  ref_id: string | null;
  created_at: string | null;
  read_at: string | null;
  read_by: string | null;
}

const ICON = {
  info: <InfoOutlinedIcon color="info" fontSize="small" />,
  success: <CheckCircleOutlineIcon color="success" fontSize="small" />,
  warning: <WarningAmberIcon color="warning" fontSize="small" />,
  error: <ErrorOutlineIcon color="error" fontSize="small" />,
};

/**
 * The console's inbox: new workspaces created by signup, signups waiting for
 * approval, uKnowva onboarding requests, failed setups. Read state is shared
 * by every operator — "one of us has seen it" is the useful fact.
 */
export function InboxTab({
  call,
  onChanged,
  onOpen,
}: {
  call: PlatformCall;
  onChanged: () => void;
  onOpen: (n: PlatformNotification) => void;
}) {
  const [items, setItems] = useState<PlatformNotification[] | null>(null);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const body = (await call(`/notifications?unread_only=${unreadOnly}&limit=200`)) as {
        items: PlatformNotification[];
      };
      setItems(body.items);
      setErr(null);
    } catch (e) {
      setErr((e as Error).message);
    }
  }, [call, unreadOnly]);

  useEffect(() => { void load(); }, [load]);

  const markRead = async (ids: number[] | null) => {
    try {
      await call('/notifications/read', {
        method: 'POST',
        body: JSON.stringify(ids ? { ids } : { all: true }),
      });
      await load();
      onChanged();
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  if (err) return <Alert severity="error">{err}</Alert>;
  if (!items) return <CircularProgress size={24} />;

  const unread = items.filter((i) => !i.read_at).length;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
        <Typography variant="subtitle1" sx={{ flex: 1 }}>
          Notifications {unread ? `· ${unread} unread` : ''}
        </Typography>
        <FormControlLabel
          control={<Switch size="small" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} />}
          label={<Typography variant="body2">Unread only</Typography>}
        />
        <Button size="small" disabled={!unread} onClick={() => void markRead(null)}>Mark all read</Button>
      </Stack>
      {items.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
          Nothing here yet.
        </Typography>
      )}
      <Stack divider={<Divider flexItem />}>
        {items.map((n) => (
          <Box
            key={n.id}
            sx={{
              display: 'flex', gap: 1.5, py: 1.25, px: 1, borderRadius: 1,
              bgcolor: n.read_at ? 'transparent' : 'action.hover',
            }}
          >
            <Box sx={{ pt: 0.25 }}>{ICON[n.severity] ?? ICON.info}</Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: n.read_at ? 400 : 600 }}>{n.title}</Typography>
              {n.body && (
                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                  {n.body}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                {fmtDate(n.created_at)}
                {n.tenant_key ? ` · ${n.tenant_key}` : ''}
                {n.read_at ? ` · read by ${n.read_by ?? 'someone'}` : ''}
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.5} alignItems="flex-start">
              {(n.ref_type === 'signup' || n.tenant_key || n.ref_type === 'provisioning_request') && (
                <Button size="small" onClick={() => { void markRead([n.id]); onOpen(n); }}>Open</Button>
              )}
              {!n.read_at && (
                <Button size="small" color="inherit" onClick={() => void markRead([n.id])}>Mark read</Button>
              )}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Paper>
  );
}
